<template>
    <bottom-dialog v-model="showDialog" max-width="550px" :dialog="!$vuetify.breakpoint.mobile" v-if="showDialog">
        <v-sheet color="primary" v-if="done" class="primarytext--text">
            <div class="fill-width d-flex">
                <div class="flex-grow-1" />
                <v-btn icon @click="showDialog = false"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
            </div>
            <div class="d-flex justify-center pt-8">
                <v-icon x-large class="primarytext--text">mdi-checkbox-marked-circle-outline</v-icon>
            </div>
            <div class="d-flex justify-center">
                <span class="text-h4">{{$translate('Done!')}}</span>
            </div>
            <div class="d-flex justify-center mt-4 pb-10">
                <span class="text-h6">{{$translate('Your cancellation is complete!')}}</span>
            </div>
        </v-sheet>
        <v-card v-else tile elevation="0">
            <v-card-title class="headline align-start flex-column pa-2 primary primarytext--text">
                <div class="font-weight-bold fill-width">
                    {{$translate('CANCEL_BOOKING')}}
                    <v-btn icon @click="showDialog = false" class="float-right"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
                </div>
            </v-card-title>
            <v-card-text class="z-no-x-scroll">
                <stepper-content v-model="currentStep" :steps="steps">
                    <template #terms>
                        <template v-if="courseBooking && cancelInfo">
                            <div class="font-weight-bold mt-2">{{$translate('Cancellation of {0}').format(courseBooking.course_name)}}</div>

                            <template v-if="cancelEnabled">
                                <p class="mt-2">
                                    {{$translate('If you proceed with the cancellation, the following terms and costs will apply.')}}
                                </p>

                                <ul>
                                    <li>{{$translate('Your cancellation is being processed today')}}, {{date}}, {{daysBeforeOrAfterStartString}}</li>
                                    <li v-if="products.length">{{$translate('Products purchased in conjunction with the booking are refunded in full price')}}</li>
                                    <li>{{$translate('You are reimbursed as follows')}}:<br><br>
                                        <div>
                                            <div class="d-flex"><div class="flex-grow-1">{{$translate('Course amount')}}</div><div>{{cancelInfo.course_amount | price}}</div></div>
                                            <div v-if="products.length" class="d-flex font-weight-bold"><div class="flex-grow-1">{{$translate('Refund of supplementary products')}}</div></div>
                                            <div class="d-flex" v-for="item in products" :key="item">
                                                <div class="flex-grow-1">{{item.name}}</div><div>{{item.amount | price}}</div>
                                            </div>
                                            <div v-if="cancelInfo.paidAmount" class="d-flex font-weight-bold"><div class="flex-grow-1">{{$translate('Paid amount')}}</div><div>{{cancelInfo.paidAmount | price}}</div></div>
                                            <div v-else class="d-flex font-weight-bold"><div class="flex-grow-1">{{$translate('The paid amount will be on the payer\'s value card')}}</div></div>
                                            <div v-if="cancelInfo.fee_fixed" class="d-flex"><div class="flex-grow-1">{{$translate('Administrative fee')}}</div><div>-{{cancelInfo.fee_fixed | price}}</div></div>
                                            <div v-if="cancelInfo.fee_by_percent" class="d-flex"><div class="flex-grow-1">{{$translate('Cost of cancellation')}} {{cancelInfo.fee_percent}}%</div><div>-{{cancelInfo.fee_by_percent | price}}</div></div>
                                            <div class="d-flex font-weight-bold"><div class="flex-grow-1">{{$translate(cancelInfo.refundAmount > 0 ? 'Amount to be refunded' : 'Amount to pay')}}</div><div>{{Math.abs(cancelInfo.refundAmount) | price}}</div></div>
                                        </div>
                                    </li>
                                </ul>
                            </template>
                            <p class="mt-2" v-else>
                                {{$translate('Please contact us to cancel this activity')}}
                            </p>

                            <div class="elevation-2 pa-4">
                                <div class="d-flex z-clickable" @click="showCancelRules = true">
                                    <v-icon color="error">mdi-sticker-alert-outline</v-icon>
                                    <div class="ml-2 error--text flex-grow-1">
                                        {{$translate('Cancellation policy')}}<br>
                                        <span class="z-small-font">{{$translate('Approved')}} {{Date.newFull(courseBooking.created).yyyymmdd()}}</span>
                                    </div>
                                    <v-icon>mdi-chevron-right</v-icon>
                                </div>

                                <cancel-rules-dialog v-model="showCancelRules" :cancel-rules="courseBooking.cancelRules" v-if="showCancelRules" />
                            </div>
                        </template>
                    </template>
                    <template #finish>
                        <div class="font-weight-bold mt-4">{{$translate('Refund method')}}</div>
                        <p>{{$translate('Choose how you would like the amount to be repaid to you.')}}</p>
                        <div class="mx-2">
                            <v-form v-model="formValid" @submit.prevent="" :disabled="loading">
                                <v-radio-group v-model="selectedMethodId">
                                    <div v-for="method in sortedMethods" :key="method.id" class="zoezi-checkout-payment-method z-rounded-border my-1">

                                        <div class="d-flex ml-2">
                                            <v-radio :label="getMethodName(method)" color="primary" :value="method.id" class="flex-grow-1 mb-0" />
                                            <img :src="method.imagekey ? $api.getImageUrl(method.imagekey, 100) : $api.getAssetUrl(method.imageSrc)" class="zoezi-checkout-payment-method-image ma-1" style="height: 32px; width: 32px" />
                                        </div>

                                        <div v-if="method.id == selectedMethodId" class="mx-2">
                                            <template v-if="method.id == 'bank'">
                                                <span class="font-weight-bold">{{cancelInfo.refundAmount | price}}</span> {{$translate('refunded to the specified account')}}
                                                <v-text-field :label="$translate('Clearing number')" autocomplete="off" v-model="clearingNumber" type="text" inputmode="numeric" min="1000" max="99999" :rules="[clearingNumberOk]">
                                                    <template v-slot:append-outer>
                                                        <v-img height=50 width=100 position="top" :src="$api.getAssetUrl('/assets/img/banks/' + bank.image)" v-if="bank && bank.image" contain/>
                                                        <span v-else-if="bank">{{bank.name}}</span>
                                                        <div v-else style="width: 20px"></div>
                                                    </template>
                                                </v-text-field>

                                                <v-text-field v-model="bankAccountNumber" type="number" autocomplete="off" inputmode="numeric" min="0" :rules="[accountNumberOk]">
                                                    <template v-slot:label>
                                                        <div>
                                                            {{$translate('Bank account number')}} <small>(<a style="pointer-events: auto" target="_blank" href="https://zoezi.se/manualer/ekonomi/autogiro-servicebyra/#kontonummer">{{$translate('Read more about bank account numbers here')}}</a>)</small>
                                                        </div>
                                                    </template>
                                                </v-text-field>
                                            </template>
                                            <template v-else-if="method.id == 'swishi'">
                                                <span class="font-weight-bold">{{cancelInfo.refundAmount | price}}</span> {{$translate('refunded to')}} {{cancelInfo.swish_account}}
                                            </template>
                                            <template v-else-if="method.id == 'valuecard'">
                                                <span class="font-weight-bold">{{cancelInfo.refundAmount | price}}</span> {{$translate('be placed on a credit note which you can use to book similar courses. If you choose this option, you cannot have your money paid out.')}}
                                            </template>
                                        </div>
                                    </div>
                                </v-radio-group>
                            </v-form>
                        </div>
                    </template>
                </stepper-content>
            </v-card-text>
            <v-card-actions>
                <stepper v-model="currentStep" :steps="steps" @cancel="showDialog=false" ref="stepper" :showTitle="steps.length > 1" />
            </v-card-actions>
        </v-card>
    </bottom-dialog>
</template>

<script>
import BottomDialog from '../components/BottomDialog.vue'
import Stepper from './Stepper.vue'
import StepperContent from './StepperContent.vue'
import CancelRulesDialog from './CancelRulesDialog.vue'
import Banks from '../services/Banks.js'
export default {
    name: 'zoezi-cancel-booking-dialog',
    props: {
        value: {},
        courseBooking: {},
        cancelEnabled: {
            type: Boolean,
            default: false
        }
    },

    components: {
        BottomDialog,
        Stepper,
        StepperContent,
        CancelRulesDialog
    },

    watch: {
        value: {
            immediate: true,
            handler(value) {
                this.showDialog = value;
            }
        },
        showDialog(value) {
            this.$emit('input', value);
        },
        courseBooking: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.getRefundInformation();
                    this.getAccounts();
                }
            }
        }
    },

    computed: {
        steps() {
            if (!this.cancelInfo) {
                return [];
            }

            if (!this.cancelEnabled) {
                return [{title: this.$translate('Terms'), id: 'terms', nextTitle: this.$translate('Close'), nextClicked: () => this.showDialog = false, complete: true}];
            }

            if (this.cancelInfo.refundAmount <= 0) {
                return [
                    {title: this.$translate('Terms'), id: 'terms', nextTitle: this.$translate('CANCEL_BOOKING'), nextLoading: this.loading, nextClicked: this.cancel, complete: true}
                ];
            } else {
                return [
                    {title: this.$translate('Terms'), id: 'terms', complete: true},
                    {title: this.$translate('Complete'), id: 'finish', nextTitle: this.$translate('CANCEL_BOOKING'), complete: this.formValid && this.selectedMethodId, nextLoading: this.loading, nextClicked: this.cancel}
                ];
            }
        },
        date() {
            let date = this.$store.state.now30s;
            let weekday = window.$translate(date.getWeekDayString())
            let lc = (x) => window.$store.state.language == 'sv' ? x.toLowerCase() : x;
            return `${lc(weekday)} ${date.getDate()} ${lc(window.$translate(date.getMonthName()))}`;
        },
        daysBeforeOrAfterStart() {
            let date = this.$store.state.now30s;
            let start = Date.newFull(this.courseBooking.startTime);
            let days = Math.abs(date.daysUntil(start));
            return days;
        },
        daysBeforeOrAfterStartString() {
            let date = this.$store.state.now30s;
            let start = Date.newFull(this.courseBooking.startTime);
            let after = date > start;
            let days = this.daysBeforeOrAfterStart;

            return this.$translate(after ? '{0} days after start' : '{0} days before start').format(days);
        },
        sortedMethods() {
            if (!this.cancelInfo) {
                return [];
            }

            return [
                {id: 'swish', name: 'Swish', imageSrc: '/assets/img/swish_logo_primary_idshape_RGB.png'},
                {id: 'swishi', name: 'Swish', imageSrc: '/assets/img/swish_logo_primary_idshape_RGB.png'},
                {id: 'bank', name: this.$translate('Bank account'), imageSrc: '/assets/img/bank.jpg'},
                {id: 'valuecard', name: this.$translate('Credit'), imageSrc: '/assets/img/valuecard.png'},
                {id: 'stripe', name: this.$translate('Card payment'), imageSrc: '/assets/img/creditcard.png'},
                {id: 'adyen-card', name: this.$translate('Card payment'), imageSrc: '/assets/img/creditcard.png'},
                {id: 'adyen-klarna', name: this.$translate('Klarna - pay later'), imageSrc: '/assets/img/klarna.svg'},
                {id: 'adyen-klarna_paynow', name: this.$translate('Klarna - pay now'), imageSrc: '/assets/img/klarna.svg'},
                {id: 'adyen-klarna_account', name: this.$translate('Klarna - account'), imageSrc: '/assets/img/klarna.svg'},
                {id: 'adyen-trustly', name: this.$translate('Trustly'), imageSrc: '/assets/img/trustly.png'},
            ].filter(method => this.cancelInfo.methods.includes(method.id));
        },
        bank() {
            return Banks.getBankFromClearingNumber(this.clearingNumber);
        },
    },

    data: () => ({
        currentStep: 0,
        showDialog: false,
        showCancelRules: false,
        formValid: false,
        bankAccountNumber: null,
        clearingNumber: null,
        selectedMethodId: null,
        loading: false,
        done: false,
        cancelInfo: null,
        products: []
    }),

    methods: {
        getMethodName(method) {
            if (method.id == 'swishi') {
                return 'Swish'
            } else if (method.id == 'stripe') {
                return this.$translate('Debit card');
            }
            return this.$translate(method.name);
        },
        clearingNumberOk(clearingNumber) {
            let bank = Banks.getBankFromClearingNumber(clearingNumber);
            if (!bank) {
                return this.$translate('No valid clearing number');
            }

            return true;
        },
        accountNumberOk() {
            let ok = Banks.validateAccountNumber(this.clearingNumber, this.bankAccountNumber);
            if (!ok) {
                return this.$translate('No valid account number');
            }

            return true;
        },
        cancel() {
            let data = {
                booking_id: this.courseBooking.id,
                method: this.selectedMethodId,
                refundAmount: this.cancelInfo.refundAmount,
            };
            if(data.method == 'bank') {
                data.account = {
                    clearingnumber: this.clearingNumber,
                    account: this.bankAccountNumber
                };
            }
            this.loading = true;
            this.$api.post('/api/memberapi/course/cancel', data).then(response => {
                this.loading = false;
                if (response.ok) {
                    this.done = true;
                } else {
                    this.$store.commit('showErrorDialog', {title: this.$translate('Cancellation error'), text: this.$translate('The booking could not be cancelled')})
                }
            })
        },
        getRefundInformation() {
            this.$api.get('/api/memberapi/course/cancelinfo', {booking_id: this.courseBooking.id}).then(info => {
                let discount = 0;
                info.payments.forEach(p => {
                    p.amount = -Number(p.amount);
                    if(!p.amount) return;
                    if(p.type == 'discount') discount += p.amount;
                    else if(p.type == 'course') return;
                    else this.products.push(p);
                })
                if(discount) this.products.push({name: this.$translate('Discount'), amount: discount});
                for(let k of ['fee', 'fee_fixed', 'fee_percent', 'fee_by_percent', 'paidAmount', 'refundAmount', 'course_amount'])
                    info[k] = Number(info[k]);
                this.cancelInfo = info;
            });
        },
        getAccounts() {
            this.$api.get('/api/memberapi/bankaccount/get').then(accounts => {
                let ba = accounts.find(x => x.type == 'bankaccount');
                if (ba) {
                    this.clearingNumber = ba.clearingnumber;
                    this.bankAccountNumber = ba.account;
                }
            });
        }
    }
}
</script>


<!--

1. Hur får jag ut betalat belopp? Rabatt och annat kan påverka det
2.



-->
